import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  SyncOutlined, EditOutlined, DeleteOutlined, AddOutlined, InfoOutlined, LocalOfferOutlined,
} from '@mui/icons-material';
import EmptyState from '../components/EmptyState';
import { useThemeMode } from '../contexts/ThemeContext';
import apiService from '../services/api';

interface PricingEntry {
  model: string;
  input_price: number;
  output_price: number;
  source: 'admin_override' | 'litellm' | 'fallback';
  last_updated?: string;
  is_verified?: boolean;
}

interface PricingData {
  models: PricingEntry[];
  last_synced?: string;
  disclaimer?: string;
}

const SOURCE = {
  admin_override: { label: 'Override', color: '#FF1A50', bg: 'rgba(255,26,80,0.1)' },
  litellm:        { label: 'LiteLLM',  color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  fallback:       { label: 'Fallback', color: '#888888', bg: 'rgba(136,136,136,0.1)' },
} as const;

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PricingPage: React.FC = () => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const card     = isDark ? '#111111' : '#FFFFFF';
  const border   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP    = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS    = isDark ? '#888888' : '#666666';
  const textM    = isDark ? '#555555' : '#AAAAAA';
  const rowHover = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';

  const [data, setData]         = useState<PricingData | null>(null);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [editOpen, setEditOpen]     = useState(false);
  const [editModel, setEditModel]   = useState('');
  const [editLocked, setEditLocked] = useState(false);
  const [editIn, setEditIn]         = useState('');
  const [editOut, setEditOut]       = useState('');
  const [saving, setSaving]         = useState(false);

  const [delModel, setDelModel] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiService.getPricing();
      // API returns { pricing: { "model": { input, output, source, ... } }, last_external_sync, ... }
      const pricingDict: Record<string, any> = res.pricing ?? {};
      const models: PricingEntry[] = Object.entries(pricingDict).map(([model, entry]) => ({
        model,
        input_price: (entry as any).input ?? 0,
        output_price: (entry as any).output ?? 0,
        source: (entry as any).source ?? 'fallback',
        last_updated: (entry as any).last_synced,
        is_verified: (entry as any).is_verified ?? false,
      }));
      models.sort((a, b) => (b.input_price + b.output_price) - (a.input_price + a.output_price));
      setData({
        models,
        last_synced: res.last_external_sync,
        disclaimer: res.disclaimer,
      });
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async (force = false) => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await apiService.syncPricing(force);
      setSyncMsg({ type: 'success', text: `Synced ${res.model_count ?? 0} models successfully` });
      await load();
    } catch {
      setSyncMsg({ type: 'error', text: 'Sync failed. Check connectivity and try again.' });
    } finally {
      setSyncing(false);
    }
  };

  const openEdit = (entry?: PricingEntry) => {
    setEditModel(entry?.model ?? '');
    setEditLocked(!!entry);
    setEditIn(entry ? String(entry.input_price) : '');
    setEditOut(entry ? String(entry.output_price) : '');
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editModel || !editIn || !editOut) return;
    setSaving(true);
    try {
      await apiService.setPricing({
        model: editModel.trim(),
        input_price: parseFloat(editIn),
        output_price: parseFloat(editOut),
        is_verified: true,
      });
      setEditOpen(false);
      await load();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (model: string) => {
    try {
      await apiService.deletePricing(model);
    } catch {
    }
    setDelModel(null);
    await load();
  };

  const models = data?.models ?? [];

  const btnPrimary = {
    bgcolor: isDark ? '#EFEFEF' : '#0A0A0A',
    color: isDark ? '#0A0A0A' : '#EFEFEF',
    borderRadius: '7px', textTransform: 'none' as const,
    fontSize: '0.82rem', fontWeight: 500,
    '&:hover': { bgcolor: isDark ? '#FFFFFF' : '#1A1A1A' },
    '&:disabled': { opacity: 0.5 },
  };

  const btnGhost = {
    bgcolor: 'transparent', color: textP, border: `1px solid ${border}`,
    borderRadius: '7px', textTransform: 'none' as const,
    fontSize: '0.82rem', fontWeight: 500,
    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP, letterSpacing: '-0.03em', lineHeight: 1.2, mb: 0.5 }}>
            Model Pricing
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: textS }}>
            {models.length} model{models.length !== 1 ? 's' : ''} · Last synced {fmtDate(data?.last_synced)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => openEdit()} startIcon={<AddOutlined sx={{ fontSize: 14 }} />} sx={{ ...btnGhost, py: 1, px: 2 }}>
            Override
          </Button>
          <Button
            onClick={() => handleSync(false)}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <SyncOutlined sx={{ fontSize: 14 }} />}
            sx={{ ...btnPrimary, py: 1, px: 2 }}
          >
            {syncing ? 'Syncing…' : 'Sync Prices'}
          </Button>
        </Box>
      </Box>

      {syncMsg && (
        <Alert severity={syncMsg.type} onClose={() => setSyncMsg(null)} sx={{ mb: 3, borderRadius: '8px', fontSize: '0.82rem' }}>
          {syncMsg.text}
        </Alert>
      )}

      {/* Disclaimer */}
      <Box sx={{
        mb: 3, px: 3, py: 1.75,
        bgcolor: isDark ? 'rgba(245,158,11,0.07)' : 'rgba(245,158,11,0.06)',
        border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px',
        display: 'flex', gap: 1.5, alignItems: 'flex-start',
      }}>
        <InfoOutlined sx={{ fontSize: 14, color: '#F59E0B', mt: 0.2, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.8rem', color: '#F59E0B', lineHeight: 1.65 }}>
          Pricing data may not reflect current rates. Admin overrides take precedence over LiteLLM cached data and hardcoded fallbacks.
          Always verify at <strong>openai.com/pricing</strong> before making billing decisions.
        </Typography>
      </Box>

      {/* Table */}
      <Box sx={{ bgcolor: card, border: `1px solid ${border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 110px 140px 140px 130px 72px',
          px: 3, py: 1.5,
          borderBottom: `1px solid ${border}`,
        }}>
          {['Model', 'Source', 'Input $/1M', 'Output $/1M', 'Updated', ''].map((col, i) => (
            <Typography key={i} sx={{ fontSize: '0.68rem', fontWeight: 600, color: textM, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {col}
            </Typography>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} sx={{ color: textM }} />
          </Box>
        ) : models.length === 0 ? (
          <EmptyState
            icon={<LocalOfferOutlined sx={{ fontSize: 20 }} />}
            code="NO_DATA · NOT_SYNCED"
            title="No pricing data"
            subtitle='Click "Sync Prices" to pull the latest model pricing from LiteLLM, or add a manual override.'
            action={{ label: 'Sync Prices', onClick: () => handleSync(false) }}
          />
        ) : (
          models.map((entry, idx) => {
            const src = SOURCE[entry.source] ?? SOURCE.fallback;
            return (
              <Box
                key={entry.model}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px 140px 140px 130px 72px',
                  px: 3, py: 1.75, alignItems: 'center',
                  borderBottom: idx < models.length - 1 ? `1px solid ${border}` : 'none',
                  '&:hover': { bgcolor: rowHover },
                  transition: 'background 0.1s',
                }}
              >
                <Typography sx={{
                  fontFamily: '"JetBrains Mono","Courier New",monospace',
                  fontSize: '0.78rem', fontWeight: 500, color: textP,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 2,
                }}>
                  {entry.model}
                </Typography>
                <Box>
                  <Box sx={{
                    display: 'inline-flex', px: 1, py: 0.25, borderRadius: '4px',
                    bgcolor: src.bg, border: `1px solid ${src.color}30`,
                  }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: src.color, letterSpacing: '0.04em' }}>
                      {src.label}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem', color: textP }}>
                  ${entry.input_price.toFixed(3)}
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem', color: textP }}>
                  ${entry.output_price.toFixed(3)}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: textS }}>
                  {fmtDate(entry.last_updated)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit override" arrow>
                    <IconButton size="small" onClick={() => openEdit(entry)} sx={{ p: 0.5, color: textM, '&:hover': { color: textP } }}>
                      <EditOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {entry.source === 'admin_override' && (
                    <Tooltip title="Remove override" arrow>
                      <IconButton size="small" onClick={() => setDelModel(entry.model)} sx={{ p: 0.5, color: textM, '&:hover': { color: '#EF4444' } }}>
                        <DeleteOutlined sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Override dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} slotProps={{ paper: { sx: { bgcolor: card, border: `1px solid ${border}`, borderRadius: '12px', boxShadow: 'none', minWidth: 380 } } }}>
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5, fontSize: '1rem', fontWeight: 700, color: textP, letterSpacing: '-0.02em' }}>
          {editLocked ? `Override: ${editModel}` : 'Add Pricing Override'}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Model name"
              value={editModel}
              onChange={e => setEditModel(e.target.value)}
              size="small"
              fullWidth
              disabled={editLocked}
              placeholder="e.g. gpt-4o-mini"
              slotProps={{ input: { sx: { fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.82rem' } } }}
            />
            <TextField
              label="Input price ($ per 1M tokens)"
              value={editIn}
              onChange={e => setEditIn(e.target.value)}
              size="small"
              fullWidth
              type="number"
              slotProps={{ htmlInput: { step: '0.001', min: '0' } }}
            />
            <TextField
              label="Output price ($ per 1M tokens)"
              value={editOut}
              onChange={e => setEditOut(e.target.value)}
              size="small"
              fullWidth
              type="number"
              slotProps={{ htmlInput: { step: '0.001', min: '0' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: textS, textTransform: 'none', fontSize: '0.82rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !editModel || !editIn || !editOut}
            sx={{ ...btnPrimary, px: 2.5, py: 0.875 }}
          >
            {saving ? 'Saving…' : 'Save Override'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delModel} onClose={() => setDelModel(null)} slotProps={{ paper: { sx: { bgcolor: card, border: `1px solid ${border}`, borderRadius: '12px', boxShadow: 'none', minWidth: 340 } } }}>
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1, fontSize: '0.95rem', fontWeight: 700, color: textP, letterSpacing: '-0.02em' }}>
          Remove Override
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography sx={{ fontSize: '0.875rem', color: textS, lineHeight: 1.65 }}>
            Remove admin override for <strong style={{ fontFamily: '"JetBrains Mono","Courier New",monospace', color: textP }}>{delModel}</strong>?
            Pricing will revert to LiteLLM or fallback data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1 }}>
          <Button onClick={() => setDelModel(null)} sx={{ color: textS, textTransform: 'none', fontSize: '0.82rem' }}>
            Cancel
          </Button>
          <Button
            onClick={() => delModel && handleDelete(delModel)}
            sx={{
              bgcolor: '#EF4444', color: '#FFFFFF', borderRadius: '7px',
              textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, px: 2,
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPage;
